import { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../../../components/feature/Navigation';
import Footer from '../../../components/feature/Footer';

export default function WinterSchoolPage() {
  const [activeFeature, setActiveFeature] = useState(0);
  const featureRefs = useRef<(HTMLDivElement | null)[]>([]);
  const navRef = useRef<HTMLDivElement>(null);
  const [isNavSticky, setIsNavSticky] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const isClickScrollingMobile = useRef(false);
  const clickScrollTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Set page title
    document.title = '미래인재학원 윈터스쿨 - 겨울방학';

    // Helper to safely update meta tags
    const updateMeta = (selector: string, attribute: string, value: string) => {
      const element = document.querySelector(selector);
      if (element) {
        element.setAttribute(attribute, value);
      } else {
        // If the meta tag does not exist, create it (robustness)
        const meta = document.createElement('meta');
        if (selector.startsWith('meta[name=')) {
          const name = selector.match(/meta\[name="([^"]+)"\]/)?.[1];
          if (name) meta.setAttribute('name', name);
        } else if (selector.startsWith('meta[property=')) {
          const prop = selector.match(/meta\[property="([^"]+)"\]/)?.[1];
          if (prop) meta.setAttribute('property', prop);
        }
        meta.setAttribute(attribute, value);
        document.head.appendChild(meta);
      }
    };

    updateMeta(
      'meta[name="description"]',
      'content',
      '미래인재학원 윈터스쿨 프로그램. 방학에도 성적 향상은 멈추지 않는다! 의정부 민락, 양주 옥정, 동두천 지행 윈터스쿨 집중 특강.'
    );
    updateMeta(
      'meta[property="og:title"]',
      'content',
      '미래인재학원 윈터스쿨 - 의정부 민락, 양주 옥정, 동두천 지행 겨울방학 특강'
    );
    updateMeta(
      'meta[property="og:description"]',
      'content',
      '미래인재학원 윈터스쿨 프로그램. 방학에도 성적 향상은 멈추지 않는다! 의정부 민락, 양주 옥정, 동두천 지행 윈터스쿨 집중 특강.'
    );
  }, []);

  const features = [
    {
      id: 1,
      number: '01',
      title: '8주간의 윈터스쿨',
      icon: 'ri-calendar-line',
      color: 'from-cyan-500 to-blue-600',
      description: '미래인재학원에서는 일반적인 타 학원 윈터 스쿨(4주)과 달리, 8주간의 체계적인 학습을 진행합니다.',
      details: []
    },
    {
      id: 2,
      number: '02',
      title: '오전 10시부터 밤 10시까지의 몰입 학습',
      icon: 'ri-time-line',
      color: 'from-sky-500 to-cyan-600',
      description: '오전 10시부터 오후 4시 30분까지는 윈터스쿨 수업을 듣고, 오후 5시 이후에는 정규 수업을 진행하여 \'오전 10시부터 밤 10시\'까지의 몰입 학습 시스템을 제공합니다.',
      details: []
    },
    {
      id: 3,
      number: '03',
      title: '정규 수업과의 긴밀한 연결',
      icon: 'ri-links-line',
      color: 'from-blue-500 to-indigo-600',
      description: '윈터스쿨 동안 다른 선생님께 배우다가 내신 기간에 선생님이 바뀌는 시스템이 아닙니다. 윈터스쿨을 진행한 담당 선생님과 내신 대비까지 꼼꼼하게 진행합니다!',
      details: []
    },
    {
      id: 4,
      number: '04',
      title: '필요한 과목 맞춤 수강',
      icon: 'ri-book-open-line',
      color: 'from-cyan-500 to-teal-600',
      description: '학생마다 필요한 과목이 다를 수 있습니다. 미래인재학원 윈터스쿨은 학생에게 필요한 다양한 과목을 개설하여, 학생이 자신에게 필요한 수업을 자유롭게 선택하여 수강할 수 있습니다.',
      details: []
    },
    {
      id: 5,
      number: '05',
      title: '자기주도학습관 + 필요한 과목 선택 수강',
      icon: 'ri-user-star-line',
      color: 'from-sky-500 to-blue-600',
      description: '자기주도학습관 + 필요한 과목 선택 수업으로 학생 개개인에게 개별화된 윈터스쿨을 제공합니다.',
      details: []
    },
    {
      id: 6,
      number: '06',
      title: '철저한 관리, 자기주도학습관',
      icon: 'ri-shield-check-line',
      color: 'from-cyan-500 to-sky-600',
      description: '항상 감독 선생님이 상주하여, 매시간 출석 체크를 진행하여 빈틈없이 관리합니다.',
      details: [
        '매시간 출석 체크를 진행하여 빈틈없이 관리합니다. (하루 총 6회 실시)',
        '넓은 책상과 시디즈 의자를 배치하여 쾌적한 최적의 학습 환경을 제공합니다.'
      ]
    },
    {
      id: 7,
      number: '07',
      title: '자기주도학습관 50% 환급',
      icon: 'ri-percent-line',
      color: 'from-blue-500 to-cyan-600',
      description: '자기주도학습관 출석률 90% 이상을 완주한 성실한 학생에게는 다음 달 교육비에서 자기주도학습관 이용료의 50%를 차감합니다. (다음 달 수강하지 않는 경우는 환급하지 않습니다)',
      details: []
    },
    {
      id: 8,
      number: '08',
      title: '자기주도학습관 완주 학생을 위한 특별 선물',
      icon: 'ri-gift-line',
      color: 'from-sky-500 to-teal-600',
      description: '자기주도학습관 8주 완주 학생들을 대상으로 아이패드, 에어팟, 치킨 등 선물 추첨 행사도 함께 진행하여, 학생들에게 끝까지 포기하지 않도록 확실하게 동기부여 합니다!',
      details: []
    }
  ];

  const featureNavItems = [
    { label: '8주 윈터스쿨', icon: 'ri-calendar-line' },
    { label: '몰입 학습', icon: 'ri-time-line' },
    { label: '정규 연결', icon: 'ri-links-line' },
    { label: '맞춤 수강', icon: 'ri-book-open-line' },
    { label: '개별화', icon: 'ri-user-star-line' },
    { label: '철저한 관리', icon: 'ri-shield-check-line' },
    { label: '50% 환급', icon: 'ri-percent-line' },
    { label: '특별 선물', icon: 'ri-gift-line' },
  ];

  const scrollToFeature = useCallback((index: number) => {
    const el = featureRefs.current[index];
    if (el) {
      const isMobile = window.innerWidth < 768;
      const offset = isNavSticky ? (isMobile ? 220 : 160) : 160;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;

      if (isMobile) {
        isClickScrollingMobile.current = true;
        if (clickScrollTimeout.current) {
          clearTimeout(clickScrollTimeout.current);
        }
        setActiveFeature(index);
        window.scrollTo({ top, behavior: 'smooth' });
        clickScrollTimeout.current = setTimeout(() => {
          isClickScrollingMobile.current = false;
        }, 1500);
      } else {
        window.scrollTo({ top, behavior: 'smooth' });
        setActiveFeature(index);
      }
    }
  }, [isNavSticky]);

  useEffect(() => {
    const handleScroll = () => {
      // Check if nav should be sticky (both mobile and desktop)
      if (sectionRef.current) {
        const sectionTop = sectionRef.current.getBoundingClientRect().top;
        setIsNavSticky(sectionTop <= 80);
      }

      // 모바일에서 클릭 스크롤 중이면 활성 탭 업데이트 건너뛰기
      const isMobile = window.innerWidth < 768;
      if (isMobile && isClickScrollingMobile.current) {
        return;
      }

      // Determine active feature based on scroll position
      const offset = isNavSticky ? (isMobile ? 250 : 200) : 200;
      for (let i = featureRefs.current.length - 1; i >= 0; i--) {
        const el = featureRefs.current[i];
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= offset) {
            setActiveFeature(i);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      if (clickScrollTimeout.current) {
        clearTimeout(clickScrollTimeout.current);
      }
    };
  }, [isNavSticky]);

  return (
    <div className="min-h-screen bg-neutral-950">
      <Navigation currentPage="curriculum" />

      {/* Hero Section */}
      <section className="relative pt-20 pb-10 md:pt-32 md:pb-20 overflow-hidden">
        {/* Winter Background */}
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900 via-neutral-900 to-neutral-950"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/10 backdrop-blur-md border border-white/20 rounded-full mb-8">
            <i className="ri-snowy-line text-cyan-300 text-base"></i>
            <span className="text-cyan-200 text-sm font-semibold tracking-wider">WINTER SCHOOL</span>
            <i className="ri-snowy-line text-cyan-300 text-base"></i>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 drop-shadow-lg">
            <span className="md:hidden">미래인재학원<br />윈터스쿨</span>
            <span className="hidden md:inline">미래인재학원 윈터스쿨</span>
          </h1>
          <p className="text-xl md:text-2xl text-cyan-100 font-medium drop-shadow-md">
            방학에도 성적 향상은 멈추지 않는다!
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section ref={sectionRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-50 border border-cyan-200 rounded-full mb-6">
              <i className="ri-snowflake-line text-cyan-600 text-sm"></i>
              <span className="text-cyan-700 text-sm font-semibold">8 FEATURES</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              미래인재학원 <br className="md:hidden" />
              윈터스쿨 <span className="text-cyan-600">특징</span>
            </h2>
            <p className="text-lg text-gray-600">
              8주간의 체계적인 학습으로 방학을 <br className="md:hidden" />
              성장의 기회로 만듭니다
            </p>
          </div>

          {/* Feature Navigation Bar */}
          <div
            ref={navRef}
            className={`${
              isNavSticky
                ? 'fixed top-[72px] left-0 right-0 z-40 bg-white/95 backdrop-blur-md shadow-md border-b border-cyan-100'
                : 'relative'
            } transition-all duration-300`}
          >
            <div className={`${isNavSticky ? 'px-3' : ''}`}>
              {/* Mobile: 4x2 Grid */}
              <div className="md:hidden py-3">
                <div className="grid grid-cols-4 gap-1.5 bg-cyan-50 p-1.5 rounded-2xl border border-cyan-200">
                  {featureNavItems.map((item, index) => (
                    <button
                      key={index}
                      onClick={() => scrollToFeature(index)}
                      className={`flex flex-col items-center gap-1 py-2.5 px-1 rounded-xl font-semibold text-xs transition-all duration-300 cursor-pointer ${
                        activeFeature === index
                          ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/25'
                          : 'text-cyan-700 hover:bg-white hover:shadow-sm'
                      }`}
                    >
                      <i className={`${item.icon} text-base`}></i>
                      <span className="whitespace-nowrap leading-tight">{item.shortLabel || item.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              {/* Desktop: Horizontal inline-flex */}
              <div className="hidden md:flex justify-center py-4">
                <div className="inline-flex gap-2 bg-cyan-50 p-1.5 rounded-full border border-cyan-200 overflow-x-auto max-w-full">
                  {featureNavItems.map((item, index) => (
                    <button
                      key={index}
                      onClick={() => scrollToFeature(index)}
                      className={`flex items-center gap-1.5 px-6 py-2.5 rounded-full font-semibold text-sm transition-all duration-300 whitespace-nowrap cursor-pointer ${
                        activeFeature === index
                          ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/25'
                          : 'text-cyan-700 hover:bg-white hover:shadow-sm'
                      }`}
                    >
                      <i className={`${item.icon} text-base`}></i>
                      <span>{item.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Spacer when nav is sticky */}
          {isNavSticky && <div className="h-16"></div>}

          {/* Features Grid */}
          <div className="space-y-8 mt-8">
            {features.map((feature, index) => (
              <div
                key={feature.id}
                ref={(el) => { featureRefs.current[index] = el; }}
                className={`relative overflow-hidden rounded-3xl border-2 ${
                  index % 2 === 0 ? 'border-cyan-200 bg-gradient-to-br from-cyan-50 to-blue-50' : 'border-blue-200 bg-gradient-to-br from-blue-50 to-sky-50'
                }`}
              >
                {/* Decorative Elements */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-cyan-200/15 to-transparent rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-blue-200/15 to-transparent rounded-full blur-3xl"></div>

                <div className="relative p-8 md:p-10">
                  <div className="flex flex-col md:flex-row gap-6 md:gap-8">
                    {/* Icon & Number + Title (mobile: side by side) */}
                    <div className="flex items-center gap-4 md:block md:flex-shrink-0">
                      <div className="relative inline-flex flex-shrink-0">
                        <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${feature.color} flex items-center justify-center shadow-lg`}>
                          <i className={`${feature.icon} text-3xl text-white`}></i>
                        </div>
                        <div className="absolute -top-3 -right-3 w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center">
                          <span className="text-sm font-black text-cyan-600">{feature.number}</span>
                        </div>
                      </div>
                      {/* Mobile only: Title next to icon */}
                      <h3 className="text-xl font-bold text-gray-900 md:hidden">
                        {feature.title}
                      </h3>
                    </div>

                    {/* Content */}
                    <div className="flex-1">
                      {/* Desktop only: Title above description */}
                      <h3 className="hidden md:block text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                        {feature.title}
                      </h3>
                      <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-4">
                        {feature.description}
                      </p>
                      
                      {feature.details.length > 0 && (
                        <ul className="space-y-3 mt-6">
                          {feature.details.map((detail, idx) => (
                            <li key={idx} className="flex items-start gap-3">
                              <div className="w-6 h-6 rounded-full bg-cyan-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                                <i className="ri-check-line text-cyan-600 text-sm"></i>
                              </div>
                              <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                                {detail}
                              </p>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 via-black to-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">윈터스쿨 상담</h2>
          <p className="text-xl text-gray-300 mb-10 leading-relaxed">
            <span className="md:hidden">
              궁금하신 점이 있으시면 문의해 주세요
            </span>
            <span className="hidden md:inline">
              궁금하신 점이 있으시면 언제든지 문의해 주세요
            </span>
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-10 py-4 bg-cyan-500 text-white text-lg font-bold rounded-full hover:bg-cyan-400 transition-all shadow-xl shadow-cyan-500/20 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-phone-line text-2xl"></i>
            상담 신청하기
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
